import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import Pagination, { usePagination } from "@/components/app/Pagination";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Clock, MessageCircle, Send, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function PiutangReminder() {
  const [data, setData] = useState({ items: [], overdue_count: 0, due_soon_count: 0, total_piutang: 0 });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | overdue | soon

  const load = async () => {
    try { const r = await api.get("/piutang?days_ahead=7"); setData(r.data); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = data.items.filter(it => {
    if (filter === "overdue" && !it.is_overdue) return false;
    if (filter === "soon" && !(it.is_due_soon && !it.is_overdue)) return false;
    const s = q.trim().toLowerCase();
    return !s || [it.no_invoice, it.nama_pembeli].some(v => String(v||"").toLowerCase().includes(s));
  });
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(filtered, 10);

  const sendEmail = async (it) => {
    if (!it.email_pembeli) { toast.error("Email pembeli belum diisi"); return; }
    try { const r = await api.post(`/piutang/${it.id}/send-reminder`); toast.success(r.data.message); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  const sendWA = (it) => {
    if (!it.no_wa_pembeli) { toast.error("No. WA pembeli belum diisi"); return; }
    const msg = `Halo *${it.nama_pembeli}*,%0A%0APengingat pembayaran invoice:%0A*${it.no_invoice}*%0A%0ATotal: ${rupiah(it.total)}%0ASisa: ${rupiah(it.sisa_tagihan)}%0AJatuh tempo: *${it.jatuh_tempo}* ${it.is_overdue?`(TERLAMBAT ${Math.abs(it.hari_tersisa)} hari)`:`(${it.hari_tersisa} hari lagi)`}%0A%0AMohon segera diselesaikan. Terima kasih.`;
    const wa = it.no_wa_pembeli.replace(/^0/, "62").replace(/\D/g, "");
    window.open(`https://wa.me/${wa}?text=${msg}`, "_blank");
  };

  return (<>
    <PageHeader title="Piutang Reminder" subtitle="Daftar tagihan yang belum lunas dengan estimasi jatuh tempo (H+30 dari tanggal invoice)."/>
    <div className="p-4 md:p-8 space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <button onClick={()=>setFilter("all")} className={`card-flat p-4 text-left ${filter==="all"?"ring-2 ring-[color:var(--brand)]":""}`} data-testid="pf-all">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Total Piutang</div>
          <div className="mt-2 font-mono text-lg md:text-2xl brand-text font-semibold">{rupiah(data.total_piutang)}</div>
          <div className="text-[11px] text-slate-400">{data.items.length} invoice</div>
        </button>
        <button onClick={()=>setFilter("overdue")} className={`card-flat p-4 text-left ${filter==="overdue"?"ring-2 ring-rose-500":""}`} data-testid="pf-overdue">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-rose-600"><AlertTriangle size={12}/> Terlambat</div>
          <div className="mt-2 font-mono text-lg md:text-2xl text-rose-600 font-semibold">{data.overdue_count}</div>
          <div className="text-[11px] text-slate-400">invoice lewat jatuh tempo</div>
        </button>
        <button onClick={()=>setFilter("soon")} className={`card-flat p-4 text-left ${filter==="soon"?"ring-2 ring-amber-500":""}`} data-testid="pf-soon">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-amber-600"><Clock size={12}/> Dekat Jatuh Tempo</div>
          <div className="mt-2 font-mono text-lg md:text-2xl text-amber-600 font-semibold">{data.due_soon_count}</div>
          <div className="text-[11px] text-slate-400">≤ 7 hari lagi</div>
        </button>
      </div>

      <div className="card-flat p-3 flex items-center gap-2">
        <Search size={14} className="text-slate-400"/>
        <Input placeholder="Cari invoice atau pembeli..." value={q} onChange={(e)=>setQ(e.target.value)} className="border-0 focus-visible:ring-0" data-testid="piutang-search"/>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs md:text-sm" data-testid="piutang-table">
            <thead>
              <tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
                <th className="p-3">Invoice</th><th className="p-3">Tanggal</th>
                <th className="p-3">Pembeli</th><th className="p-3 text-right">Total</th>
                <th className="p-3 text-right">Sisa</th><th className="p-3">Jatuh Tempo</th>
                <th className="p-3">Status</th><th className="p-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-slate-400">Tidak ada piutang.</td></tr>}
              {paged.map(it => (
                <tr key={it.id} className={`border-b hover:bg-slate-50 ${it.is_overdue?"bg-rose-50/40":it.is_due_soon?"bg-amber-50/40":""}`}>
                  <td className="p-3 font-mono text-[12px]">{it.no_invoice}</td>
                  <td className="p-3 whitespace-nowrap">{tanggalID(it.tanggal)}</td>
                  <td className="p-3">{it.nama_pembeli}</td>
                  <td className="p-3 text-right font-mono">{rupiah(it.total)}</td>
                  <td className="p-3 text-right font-mono text-rose-600 font-semibold">{rupiah(it.sisa_tagihan)}</td>
                  <td className="p-3 whitespace-nowrap">{it.jatuh_tempo}</td>
                  <td className="p-3">
                    {it.is_overdue ? (
                      <span className="text-[11px] px-2 py-0.5 rounded-sm bg-rose-100 text-rose-700 font-semibold">Terlambat {Math.abs(it.hari_tersisa)}h</span>
                    ) : it.is_due_soon ? (
                      <span className="text-[11px] px-2 py-0.5 rounded-sm bg-amber-100 text-amber-700 font-semibold">H-{it.hari_tersisa}</span>
                    ) : (
                      <span className="text-[11px] px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600">H-{it.hari_tersisa}</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={()=>sendWA(it)} className="h-7 rounded-sm text-[11px]" data-testid={`piutang-wa-${it.id}`}>
                        <MessageCircle size={12} className="mr-1 text-emerald-600"/>WA
                      </Button>
                      <Button size="sm" variant="outline" onClick={()=>sendEmail(it)} className="h-7 rounded-sm text-[11px]" data-testid={`piutang-email-${it.id}`}>
                        <Send size={12} className="mr-1 brand-text"/>Email
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="piutang-pagination"/>
      </div>
    </div>
  </>);
}
